import axios from 'axios';
import {addPopup, requestEnded, requestStarted} from '../redux/actions';
import * as popups from '../constants/popup-types';
import AnalyticsManager from '../app/analytics'
import AppConfig from '../app/config/AppConfig';
import store from '../redux';
import { buildRequestMutexKey, runWithRequestMutex } from '../redux/api/requestMutex';

class baseRequest {
    static lastOtp = '000000';

    constructor() {
        this.Config = new AppConfig();
        this.useNewResponseFormat = false;
    }


    updateLastOtp(code) {
        baseRequest.lastOtp = code
    }

    request({url, method, params, data, headers}) {
        const sessionToken = store.getState().tokens.sessionToken;
        const mutexKey = buildRequestMutexKey(method, url);

        return runWithRequestMutex(mutexKey, (releaseMutex) => {
            return axios.request({
                baseURL: this.Config.full_path,
                method,
                url,
                params: this.appendQueryParams(params),
                data: {...data, sessionToken},
                withCredentials: true,
                headers: {
                    'x-xsrf-token': 1,
                    ...headers,
                }
            }).then((response) => {
                releaseMutex();
                this.response = response.data;
                if (this.isValidResponse(response.status, response.data)) {
                    this.parseResponse(response.data);
                } else {
                    this.onFailure(response.data);
                }
                this.dispatch(requestEnded());
            })
                .catch((error) => {
                    releaseMutex();
                    AnalyticsManager.logException(error);
                    this.dispatch(requestEnded());
                    this.handleServerError(error);
                });
        });
    }

    get(url, params = {}, headers = {}) {
        this.request({
            method: 'GET',
            url,
            params,
            headers
        })
    }

    post(url, params, body, headers = {}) {
        const sessionToken = store.getState().tokens.sessionToken;
        this.request({
            method: 'POST',
            url,
            params,
            data: {...body, sessionToken},
            headers,
        })
    }

    isValidResponse(status, data) {
        return !(status >= 400 || data.status >= 400 || data.error);

    }

    appendQueryParams(params = {}) {
        try {
            const search = new URLSearchParams(window.location.search || '')
            return {...params, ...Object.fromEntries(search)}
        } catch (e) {
            return params;
        }
    }

    call(methodName, params = {}, method = 'post', options = {}) {
        const sessionToken = store.getState().tokens.sessionToken;
        let data = new FormData();
        let settings = {
            withCredentials: true,
            params: {},
            headers: {
                // X_OTP: baseRequest.lastOtp,
                ...options.headers,
                'x-xsrf-token': 1
            },
        };

        // Only here sending the methodName so the "/" would be chained to the name if needed
        let requestUrl = this.buildMethodUrl(methodName);

        // If there's an alternative endpoint for this method, use it
        if ('basePath' in options) {
            requestUrl = `${options.basePath}/${methodName}`;
        } else if (window.GetMethods) {
            if (window.GetMethods.methods.includes(methodName)) {
                method = "get";
                requestUrl = window.GetMethods.endpoint + `${methodName}/`;
            }
        }

        // Checks if method is post - creates the data to send as form data
        if (method === "post") {
            if (
                !(Object.entries(params).length === 0 && params.constructor === Object)
            ) {
                for (let key in params) {
                    data.append(key, params[key]);
                }
                data.append('sessionToken', sessionToken)
            } else {
                data = null;
            }

            // Then adding the created data to the settings
            settings.data = data;

            if (options.stringifyData) {
                settings.data = new URLSearchParams(data);
            }
        }

        // If method is get adding the params and adding "/" at the end of the method name
        else if (method === "get") {
            settings.params = params;
        }

        if (this.Config.debug_mode) {
            console.log("%cmaking request:" + requestUrl, "color: #0000FF", "");
            console.log("%c---request payload:", "color: #0000FF", params);
        }


        if (!requestUrl.endsWith('/')) {
            requestUrl = requestUrl + '/'
        }

        settings.params = this.appendQueryParams(settings.params || {})
        settings.method = method;
        settings.url = requestUrl;

        const mutexKey = buildRequestMutexKey(method, requestUrl);

        return runWithRequestMutex(mutexKey, (releaseMutex) => {
            this.dispatch(requestStarted());

            return axios(settings)
                .then((response) => {
                    releaseMutex();
                    this.response = response.data;
                    if (this.useNewResponseFormat ? response.data.statusCode : response.data.status) {
                        this.parseResponse(response.data);
                    } else {
                        this.onFailure(response.data, methodName);
                    }
                    this.dispatch(requestEnded());
                    return response.data;
                })
                .catch((error) => {
                    releaseMutex();
                    AnalyticsManager.logException(error);
                    this.dispatch(requestEnded());
                    this.handleServerError(error);
                    return error?.response?.data;
                });
        });
    }

    //check api response status
    parseResponse(response) {
        (
            this.useNewResponseFormat
                ? response.statusCode >= 200 && response.statusCode < 300
                : response.status === 1
        ) ? this.onSuccess(response) : this.onFailure(response);
    }

    //Generic success method. Override in child class for custom behaviour.
    onSuccess() {
        console.log(this.name, " successful");
    }

    //Generic failure method. Override in child class for custom behaviour.
    onFailure(response, methodName = "") {
        let err_msg = "תקלת שרת, אנא נסה שנית מאוחר יותר";
        if (response.err?.content) {
            err_msg = response.err.content;
        }
        this.dispatch(
            addPopup({
                type: popups.GENERAL_MESSAGE,
                text: err_msg,
                popup_id: Math.round(Math.random() * 1000000),
            })
        );
        // console.log('%cRequest Failed: ' + this.name + ' in baseRequest onFailure', 'color: #FF0000');
    }

    //request utility functions
    buildBaseUrl() {
        return (
            "//" +
            this.Config.api.baseUrl +
            "/" +
            this.Config.api.path +
            "/" +
            this.Config.api.platform +
            "/" +
            this.Config.api.version +
            "/"
        );
    }

    buildMethodUrl(methodName) {
        return this.buildBaseUrl() + methodName;
    }

    mergeRequestSettings(settings) {
        let requestSettings = "";

        for (let key in settings) {
            if (key in settings) {
                requestSettings[key] = settings[key];
            }
        }

        return requestSettings;
    }

    handleServerError(error) {
        this.dispatch(addPopup({type: popups.API_MESSAGE, text: error.message}));
    }
}

export default baseRequest;
